import type { SymbolKey } from '@/entities/symbol';
import { takeItemBasedOnOrder } from '@/entities/symbol';
import type { SymbolPreviewItem } from '@/features/_shared/SymbolsPreview';
import isHumanMessage from '@/features/ai-chat/lib/isHumanMessage';

import type { MessagePart, UIMessage } from '@tanstack/ai-client';

export const GX_REGEX = /GX\d{7}/g;
const GX_LINKABLE_REGEX = /<(GX\d{7})>|\((GX\d{7})\)|(?<![\w[/])(GX\d{7})(?!\w)/g;

export function linkifyGxCodes(content: string): string {
  return content.replace(
    GX_LINKABLE_REGEX,
    (
      _match,
      angleWrappedCode: string | undefined,
      parenthesizedCode: string | undefined,
      bareCode: string | undefined
    ) => {
      const code = angleWrappedCode ?? parenthesizedCode ?? bareCode;
      return code ? `[${code}](/${code})` : _match;
    }
  );
}

function rowsFromToolOutput(output: unknown): Array<{ code?: unknown }> {
  if (!output || typeof output !== 'object' || !('rows' in output)) return [];
  const rows = (output as { rows?: unknown }).rows;
  return Array.isArray(rows) ? rows : [];
}

const extractCodesFromPart = (part: MessagePart): string[] => {
  if (part.type === 'text') {
    return part.content.match(GX_REGEX) ?? [];
  }
  if (part.type === 'tool-call' && part.state === 'complete') {
    return rowsFromToolOutput(part.output)
      .map(row => (typeof row.code === 'string' ? row.code.trim() : ''))
      .filter(Boolean);
  }
  return [];
};

export function extractGxCodesFromMessage(msg: UIMessage): string[] {
  if (isHumanMessage(msg)) return [];
  // `isHumanMessage`'s predicate type equals its parameter type (`UIMessage`), so on the
  // false branch TS narrows `msg` to `never` (it cannot express "not this exact type").
  // Re-widen to `UIMessage` — the predicate already proved this is not a human message.
  const aiMsg = msg as UIMessage;
  try {
    const raw = aiMsg.parts.flatMap(extractCodesFromPart);
    return [...new Set(raw)];
  } catch {
    return [];
  }
}

type CatalogueApiItem = {
  Code: string;
  PeriodType: string;
  Period?: string;
  TimeRef: string;
  Alias?: string;
  Currency?: string;
  Units?: string;
  Title?: string;
};

const EMPTY_SYMBOL: SymbolKey = { Code: '', PeriodType: '', Period: '', TimeRef: '' };

export function toCatalogueItem(dataArr?: CatalogueApiItem[]): SymbolPreviewItem {
  if (!dataArr || dataArr.length === 0) {
    return {
      symbol: EMPTY_SYMBOL,
      label: '',
      metadata: { Currency: '', Units: '', Title: '' }
    };
  }

  const bestKeyPerCode = dataArr.reduce<Partial<Record<string, SymbolKey>>>((acc, data) => {
    const key: SymbolKey = {
      Code: data.Code,
      PeriodType: data.PeriodType,
      Period: data.Period ?? '1',
      TimeRef: data.TimeRef
    };
    const existing = acc[key.Code];
    acc[key.Code] = existing ? takeItemBasedOnOrder(existing, key) : key;
    return acc;
  }, {});

  const { Alias, Currency = '', Units = '', Title = '' } = dataArr[0];

  return {
    symbol: Object.values(bestKeyPerCode)[0] ?? EMPTY_SYMBOL,
    label: '',
    metadata: { Alias, Currency, Units, Title }
  };
}

export function gxQueryKey(code: string) {
  return ['gxCodeAIChat', code] as const;
}
